import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useToast } from "@/components/ui/use-toast";
import {
  createSubscription,
  updateSubscription,
} from "../store/membership/membershipActions";
import { getUserSubscription, refreshUser } from "../store/auth/authActions";
import { getEffectivePlan } from "../utils/subscriptionUtils";

export const useUpgradeLogic = (newPlan, isTrial) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { toast } = useToast();
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const { user, currentSubscription, defaultPaymentMethod } = useSelector(
    (state) => state.auth,
  );
  const { isSubscriptionLoading } = useSelector((state) => state.membership);

  const currentPlan = getEffectivePlan(currentSubscription);

  /**
   * Main CTA on the upgrade page.
   * - Trial path: creates subscription with isTrial:true, no payment method.
   * - Paid path: opens payment method selector modal (existing card) or redirects to /payment (new card).
   */
  const handleUpgradeClick = async () => {
    if (isTrial) {
      await handleTrialStart();
    } else if (!defaultPaymentMethod?.id) {
      handleNewCardPayment();
    } else {
      setIsPaymentModalOpen(true);
    }
  };

  /**
   * Starts a 30-day free trial — no payment method needed.
   * Calls createSubscription with isTrial:true.
   */
  const handleTrialStart = async () => {
    setIsProcessing(true);
    try {
      await dispatch(
        createSubscription({
          payload: {
            userId: user.uid,
            planId: newPlan?.id,
            isTrial: true,
          },
        }),
      ).unwrap();

      await dispatch(refreshUser()).unwrap();
      await dispatch(getUserSubscription(user?.uid)).unwrap();

      toast({
        title: `🎉 Your 30-day free trial for ${newPlan?.name} has started!`,
        description:
          "You have full access for 30 days — no payment needed. We'll remind you before your trial ends.",
        variant: "success",
        duration: 5000,
      });

      navigate("/dashboard", {
        state: { plan: newPlan?.id, planSelected: true, fromTrial: true },
      });
    } catch (error) {
      console.error("Trial start failed:", error);
      toast({
        title: "Trial Start Failed",
        description:
          "There was an error starting your trial. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleExistingCardPayment = async () => {
    setIsProcessing(true);
    try {
      const payload = {
        subscriptionId: currentSubscription?.stripeSubscriptionId,
        newPriceId: newPlan?.stripePriceId,
        paymentMethodId: defaultPaymentMethod.id,
      };

      await dispatch(updateSubscription({ payload })).unwrap();
      await dispatch(refreshUser()).unwrap();
      await dispatch(getUserSubscription(user?.uid)).unwrap();

      toast({
        title: `Successfully ${currentPlan ? "Upgraded" : "Subscribed"} to ${newPlan?.name}`,
        description:
          "Your membership has been upgraded successfully. Redirecting to your dashboard...",
        variant: "success",
        duration: 3000,
      });

      navigate("/dashboard", {
        state: { plan: newPlan?.id, planSelected: true },
      });
    } catch (error) {
      console.error("Error processing payment:", error);
      toast({
        title: "Upgrade Failed",
        description:
          "There was an error processing your upgrade. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
      setIsPaymentModalOpen(false);
    }
  };

  const handleNewCardPayment = () => {
    setIsPaymentModalOpen(false);
    toast({
      title: `Upgrading to ${newPlan.name}`,
      description:
        "You will be redirected to the payment page to complete the upgrade.",
      variant: "default",
    });
    navigate("/payment", {
      state: {
        plan: newPlan,
        isUpgrade: currentSubscription?.id ? true : false,
      },
    });
  };

  const handleModalClose = () => {
    if (!isProcessing) {
      setIsPaymentModalOpen(false);
    }
  };

  return {
    isPaymentModalOpen,
    isProcessing,
    isSubscriptionLoading,
    handleUpgradeClick,
    handleExistingCardPayment,
    handleNewCardPayment,
    handleModalClose,
  };
};
